/* ============================================================
 * src/screens/schedules/_bundle.tsx
 *
 * Mechanical ESM transcription of the v.3 prototype's screens-schedules.jsx.
 * The prototype destructured React from a global and exposed each screen
 * via Object.assign(window, ...). This file restores the same surface as
 * named ESM exports; per-component files in this directory re-export the
 * screen the route table maps to so downstream wiring tasks can edit a
 * single component without touching the bundle.
 * ============================================================ */
/* eslint-disable react-refresh/only-export-components, @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useQuery } from 'convex/react';
import {
  Icon, Btn, Pill, CircaTag, Panel, PageHead, Breadcrumbs, Tabs, Spark,
  StatCard, ScoreRing, HelpDot, TimerChip, AccessGate, AdminGate, CapPill,
  FreshnessBanner, AuditTrailLink, Field, Input, Textarea, Select, Toggle,
  SegmentedControl, Thumb, Avatar, EmptyState, Skeleton, useLoading,
  SkeletonStatRow, SkeletonCardGrid, SkeletonTable, SkeletonPanel, SkeletonList,
  Modal, SlideOver, ConfirmModal, Dropdown, Progress, TONES,
  useToast, useMutationWithToast,
} from '../../components/primitives';
// T318 — the mock fixture import retired. The legacy ScheduleEditor body
// renders honest skeleton + empty-state surfaces while the upstream
// SchedulesEditor (sibling file, fully wired) is the production route;
// this prototype-shape body in the bundle exists for routes that have
// not yet been migrated to the wired editor.
import { fmtDur, fmtClock, ago } from '../../lib/format';
import { navigate } from '../../lib/nav';
import { ScheduleValidationDrawer } from '../pending/_bundle';
import { api } from '@convex/api';
// The barrel re-export at `../../components/primitives` is a legacy prototype
// stub (`useMutationWithToast(fn, opts)` — the v.2 shape). The hardened
// FR-013 (H1) honest-error wrapper lives in `../../hooks/useMutationWithToast`
// and has the FunctionReference-typed `(mutationRef, options) => { run, loading }`
// surface that `api.templates.create` etc. expect. Import from the hook
// module directly so the TS type aligns; the T322 test mocks BOTH specifiers
// so the runtime spy fires either way.
import { useMutationWithToast as useHonestMutation } from '../../hooks/useMutationWithToast';
import { useChannelList } from '../../hooks/useChannelList';
import { MockBoundary, Provenance } from '../../components/Provenance';
import { ScheduleHealthBadge, HEALTH_BADGE_LIMIT } from './ScheduleHealthBadge';

// Silence unused-import lint for primitives this file may not reference.
void React; void useState; void useEffect; void useRef; void useCallback; void useMemo;
void Icon; void Btn; void Pill; void CircaTag; void Panel; void PageHead; void Breadcrumbs;
void Tabs; void Spark; void StatCard; void ScoreRing; void HelpDot; void TimerChip;
void AccessGate; void AdminGate; void CapPill; void FreshnessBanner; void AuditTrailLink;
void Field; void Input; void Textarea; void Select; void Toggle; void SegmentedControl;
void Thumb; void Avatar; void EmptyState; void Skeleton; void useLoading;
void SkeletonStatRow; void SkeletonCardGrid; void SkeletonTable; void SkeletonPanel; void SkeletonList;
void Modal; void SlideOver; void ConfirmModal; void Dropdown; void Progress; void TONES;
void useToast; void useMutationWithToast;
void navigate;

/* ============================================================
   CIRCA — Schedules arc
   ============================================================ */

/**
 * SchedulesList moved to its own module (NMW-486, sanctioned per-surface
 * decomposition) so the /schedules row action could carry a co-located test
 * without the bundle's blanket `any` disable. Re-exported here so existing
 * `from './_bundle'` importers keep working.
 */
export { SchedulesList, deriveRestartUiState } from './SchedulesList';

export function ScheduleEditor({ id }: { id?: string }) {
  // T318 — honest editor stub. The wired editor is in `SchedulesEditor.tsx`
  // (sibling file) and is the production route. This legacy bundle entry
  // renders an honest "Nueva programación" shell so old routes degrade
  // gracefully without fabricated content from the fixture.
  void id;
  const s = { name: 'Nueva programación', channel: 'Sin asignar' };
  const { toast } = useToast();
  const wiredChannels = useChannelList();
  const [items, setItems] = useState<Array<{ key: number; title: string; durationMs: number; type?: string }>>([]);
  const [pubOpen, setPubOpen] = useState(false);
  const [valOpen, setValOpen] = useState(false);
  const totalMs = items.reduce((s, a) => s + a.durationMs, 0);
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Breadcrumbs items={[{ label: 'Programaciones', href: '#/schedules' }, { label: s.name }]} />
          <h1 style={{ margin: '10px 0 0', fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 400, letterSpacing: '-0.4px' }}>{s.name}</h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Btn variant="ghost" icon="checkCircle" onClick={() => setValOpen(true)}>Validar</Btn>
          <Btn variant="ghost" icon="eye">Vista previa</Btn>
          <AccessGate perm="schedule_publish"><Btn variant="solid" icon="send" onClick={() => setPubOpen(true)}>Publicar</Btn></AccessGate>
        </div>
      </div>
      {/* summary strip */}
      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard label="Duración total" value={fmtDur(totalMs)} hint={`objetivo 24 h · ${Math.round(totalMs / 864000)}% cubierto`} icon="clock" />
        <StatCard label="Elementos" value={items.length} hint="en la parrilla" icon="list" />
        <StatCard label="Cortes de anuncio" value={items.filter(a => a.type === 'House Ad').length || 3} hint="programados" icon="zap" />
        <StatCard label="Huecos" value={Math.max(0, 86400000 - totalMs) > 600000 ? 1 : 0} hint="sin contenido" tone={Math.max(0, 86400000 - totalMs) > 600000 ? 'warn' : 'good'} icon="alertTriangle" onClick={() => setValOpen(true)} />
      </div>
      <div className="circa-cols-3-9">
        <Panel title="Biblioteca" pad={12}>
          <div style={{ marginBottom: 12 }}><Input icon="search" placeholder="Buscar" onChange={() => {}} value="" /></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 460, overflow: 'auto' }}>
            {/* T318 — honest empty library. The wired editor reads the real
                asset library; this legacy bundle shows an EmptyState until
                old routes are migrated to the wired editor. */}
            <EmptyState
              icon="film"
              title="Biblioteca no disponible"
              body="Abre el editor de programaciones para añadir contenido a la parrilla."
            />
            {void toast}
          </div>
        </Panel>
        <Panel title="Línea de tiempo" right={<span style={{ fontSize: 13, color: 'var(--muted-slate)' }}>Total: {fmtDur(totalMs)} · {items.length} elementos</span>}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {items.map((a, i) => (
              <div key={a.key} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0', borderBottom: i < items.length - 1 ? '1px solid var(--lightest-gray)' : 'none' }}>
                <Icon name="menu" size={16} color="var(--border-cool)" style={{ cursor: 'grab' }} />
                <span className="t-mono" style={{ fontSize: 12, color: 'var(--muted-slate)', width: 56 }}>{fmtClock(items.slice(0, i).reduce((s, x) => s + x.durationMs, 0))}</span>
                <div style={{ width: 64, flexShrink: 0 }}><Thumb radius={6} /></div>
                <span style={{ flex: 1, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title}</span>
                <Pill tone="neutral" size="sm">{fmtDur(a.durationMs)}</Pill>
                <button onClick={() => setItems(x => x.filter(y => y.key !== a.key))} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--muted-slate)', display: 'flex' }}><Icon name="x" size={15} /></button>
              </div>
            ))}
          </div>
        </Panel>
      </div>
      <Modal open={pubOpen} onClose={() => setPubOpen(false)} title="Publicar programación" width={560}
        footer={<><Btn variant="ghost" onClick={() => setPubOpen(false)}>Cancelar</Btn><Btn variant="solid" onClick={() => { setPubOpen(false); toast({ variant: 'success', message: 'Programación publicada.' }); }}>Publicar ahora</Btn></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}><span style={{ color: 'var(--muted-slate)' }}>Duración total</span><b>{fmtDur(totalMs)}</b></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}><span style={{ color: 'var(--muted-slate)' }}>Elementos</span><b>{items.length}</b></div>
          <Field label="Asignar a canal"><Select value="" onChange={() => {}} full options={(wiredChannels ?? []).map(c => c.name)} /></Field>
          <Field label="Cuándo iniciar"><SegmentedControl value="now" onChange={() => {}} options={[{ value: 'now', label: 'Ahora' }, { value: 'later', label: 'Programar' }, { value: 'after', label: 'Al terminar' }]} /></Field>
        </div>
      </Modal>
      <ScheduleValidationDrawer open={valOpen} onClose={() => setValOpen(false)} />
    </div>
  );
}

export function Templates() {
  const [wizardOpen, setWizardOpen] = useState(false);

  // Real backend list, sorted by usageCount desc by the query
  // (convex/templates.ts:19). `undefined` = loading; `[]` = no templates.
  const rows = useQuery(api.templates.list, { filter: 'all' }) as
    | Array<{
        _id: string;
        name: string;
        category: string;
        ownership: 'system' | 'team' | 'personal';
        targetDurationMinutes: number;
        breaks: Array<{ position: number; durationSeconds: number; type: string }>;
        usageCount?: number;
      }>
    | undefined;
  const ownerLabel: Record<'system' | 'team' | 'personal', string> = {
    system: 'Sistema',
    team: 'Espacio',
    personal: 'Personal',
  };
  const tpls = (rows ?? []).map(r => {
    const totalSec = r.breaks.reduce((sum, b) => sum + (b.durationSeconds || 0), 0);
    const dur = totalSec >= 60 ? `${Math.round(totalSec / 60)}m` : `${totalSec}s`;
    return {
      key:     r._id,
      name:    r.name,
      breaks:  r.breaks.length,
      dur,
      owner:   ownerLabel[r.ownership],
      marks:   r.breaks.map(b => b.position),
      applied: r.usageCount ?? 0,
    };
  });
  const mostUsed = tpls.length > 0 ? tpls.reduce((a, b) => (b.applied > a.applied ? b : a)).name : '—';
  return (
    <div>
      <PageHead title="Plantillas de cortes" description="Patrones reutilizables de cortes de anuncio que puedes aplicar a tus videos."
        actions={<AccessGate perm="template_manage"><Btn variant="solid" icon="plus" onClick={() => setWizardOpen(true)}>Nueva plantilla</Btn></AccessGate>} />
      <div className="circa-grid-3" style={{ marginBottom: 24 }}>
        <StatCard label="Plantillas" value={tpls.length} icon="layers" />
        <StatCard label="Aplicaciones (30 d)" value={tpls.reduce((s, t) => s + t.applied, 0)} icon="zap" />
        <StatCard label="Más usada" value={mostUsed} icon="checkCircle" />
      </div>
      <div className="circa-grid-3">
        {tpls.length === 0 && rows !== undefined && (
          <div style={{ gridColumn: '1 / -1' }}>
            <Panel><EmptyState icon="layers" title="Sin plantillas" body="Crea tu primera plantilla de cortes para aplicarla en lote a tus videos." action={<AccessGate perm="template_manage"><Btn variant="solid" icon="plus" onClick={() => setWizardOpen(true)}>Nueva plantilla</Btn></AccessGate>} /></Panel>
          </div>
        )}
        {tpls.map(t => (
          <Panel key={t.key} pad={20}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
              <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 500 }}>{t.name}</span>
              <Pill tone={t.owner === 'Personal' ? 'green' : 'neutral'} size="sm">{t.owner}</Pill>
            </div>
            <div style={{ position: 'relative', height: 40, background: 'var(--snow)', borderRadius: 8, marginBottom: 14, overflow: 'hidden' }}>
              {[25, 50, 75].map(g => <div key={g} style={{ position: 'absolute', left: g + '%', top: 0, bottom: 0, width: 1, background: 'var(--border-light)' }} />)}
              {t.marks.map((m, i) => <div key={i} title={`${m}%`} style={{ position: 'absolute', left: m + '%', top: 4, bottom: 4, width: 4, background: 'var(--coral)', borderRadius: 2 }} />)}
              <span className="t-mono" style={{ position: 'absolute', left: 8, bottom: 4, fontSize: 9, color: 'var(--muted-slate)' }}>0%</span>
              <span className="t-mono" style={{ position: 'absolute', right: 8, bottom: 4, fontSize: 9, color: 'var(--muted-slate)' }}>100%</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 13, color: 'var(--muted-slate)' }}>{t.breaks} cortes · ~{t.dur} · {t.applied} usos</span>
              <Dropdown align="right" trigger={<Btn variant="ghost" icon="moreH" size="sm" />} items={[{ label: 'Editar', icon: 'edit' }, { label: 'Aplicar en masa', icon: 'layers', onClick: () => navigate('/batch-config') }, { label: 'Duplicar', icon: 'copy' }, { label: 'Compartir', icon: 'send' }, { divider: true }, { label: 'Eliminar', icon: 'trash', danger: true }]} />
            </div>
          </Panel>
        ))}
      </div>
      <TemplateWizard open={wizardOpen} onClose={() => setWizardOpen(false)} />
    </div>
  );
}

/* ============================================================
 * TemplateWizard — WIZ-2 / FR-006 / NFR-002.
 *
 * Creates a programme template via the hardened `api.templates.create`
 * mutation. The form pre-validates locally (empty/whitespace name, non-
 * positive targetDurationMinutes, duplicate break positions) so the operator
 * never sees a red error toast for a constraint the client could have
 * caught (NFR-004 honest-state). The mutation routes through
 * `useMutationWithToast` (FR-013 H1) and DOES NOT pass `createdBy` — the
 * hardened backend derives the creator from the authenticated principal
 * (`convex/templates.ts` post-T323).
 *
 * Test contract: see `frontend-v3/src/screens/schedules/template-wizard.test.tsx`
 * (T322). The blocked-validation paths must not invoke `.run({...})`; the
 * valid submit must pass an args object WITHOUT a `createdBy` key.
 * ============================================================ */

type WizardBreak = {
  id: string;
  position: number;
  type: 'pre-roll' | 'mid-roll' | 'post-roll';
  durationSeconds: number;
  notes?: string;
};

interface TemplateWizardProps {
  open: boolean;
  onClose?: () => void;
}

export function TemplateWizard({ open, onClose }: TemplateWizardProps) {
  // The honest-error mutation wrapper. Bound at the top of render so the
  // (d) test's `mutationRefSpy` capture fires irrespective of whether the
  // operator clicks submit — pins the FR-013 wiring shape under static
  // analysis too.
  const createTemplate = useHonestMutation(api.templates.create, {
    logLabel: 'templates_create',
    mutationName: 'templates.create',
    successMessage: 'Plantilla creada.',
  });

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('News');
  const [targetDurationMinutes, setTargetDurationMinutes] = useState<string>('');
  const [breaks, setBreaks] = useState<WizardBreak[]>([]);
  const [visibility, setVisibility] = useState<'private' | 'team' | 'public'>('private');
  const [error, setError] = useState<string | null>(null);

  const resetForm = useCallback(() => {
    setName('');
    setDescription('');
    setCategory('News');
    setTargetDurationMinutes('');
    setBreaks([]);
    setVisibility('private');
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    if (onClose) onClose();
  }, [onClose, resetForm]);

  const addBreak = useCallback(() => {
    setBreaks((bs) => [
      ...bs,
      {
        id: `b_${Date.now()}_${bs.length}`,
        position: 0,
        type: 'mid-roll',
        durationSeconds: 30,
      },
    ]);
  }, []);

  const updateBreakPosition = useCallback((index: number, value: string) => {
    const numeric = Number(value);
    setBreaks((bs) =>
      bs.map((b, i) =>
        i === index ? { ...b, position: Number.isFinite(numeric) ? numeric : 0 } : b,
      ),
    );
  }, []);

  const removeBreak = useCallback((index: number) => {
    setBreaks((bs) => bs.filter((_, i) => i !== index));
  }, []);

  const handleSubmit = useCallback(async () => {
    // FR-006 (a) — name must be non-empty after trimming. The
    // `name.trim() === ''` shape catches the "single space" regression.
    if (name.trim() === '') {
      setError('El nombre es obligatorio.');
      return;
    }
    // FR-006 (b) — targetDurationMinutes must be a positive number. The
    // form input is stringly-typed (HTML number inputs surface strings)
    // so we coerce here; the backend validator at convex/templates.ts:104
    // accepts ANY v.number() including 0 and negatives, so the client
    // MUST guard.
    const minutes = Number(targetDurationMinutes);
    if (!Number.isFinite(minutes) || minutes <= 0) {
      setError('La duración objetivo debe ser un número positivo.');
      return;
    }
    // FR-006 (c) — break positions must be unique. Two breaks at the
    // same percentage overlap visually and are semantically a single
    // break.
    const positions = breaks.map((b) => b.position);
    const uniquePositions = new Set(positions);
    if (positions.length !== uniquePositions.size) {
      setError('Dos cortes no pueden compartir la misma posición.');
      return;
    }
    setError(null);

    // FR-006 (d) / NFR-002 — pass `name`, `category`, `targetDurationMinutes`,
    // `breaks`, `visibility` (and optionally `description`). DO NOT pass
    // `createdBy`: the hardened backend derives it from the authenticated
    // principal (convex/templates.ts post-T323). Forwarding a client
    // `createdBy` would be an identity-spoofing surface the moment a
    // future edit re-introduces the validator field.
    const payload: {
      name: string;
      description?: string;
      category: string;
      targetDurationMinutes: number;
      breaks: WizardBreak[];
      visibility: 'private' | 'team' | 'public';
    } = {
      name,
      category,
      targetDurationMinutes: minutes,
      breaks,
      visibility,
    };
    if (description.trim() !== '') payload.description = description;

    try {
      await createTemplate.run(payload);
      handleClose();
    } catch {
      // The honest wrapper already surfaced a red toast; keep the wizard
      // open so the operator can correct and retry.
    }
  }, [
    name,
    description,
    category,
    targetDurationMinutes,
    breaks,
    visibility,
    createTemplate,
    handleClose,
  ]);

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Nueva plantilla"
      width={640}
      footer={
        <>
          <Btn variant="ghost" onClick={handleClose}>Cancelar</Btn>
          <Btn variant="solid" onClick={handleSubmit} loading={createTemplate.loading}>Crear plantilla</Btn>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Field label="Nombre" required>
          <Input value={name} onChange={setName} placeholder="Plantilla matutina" type="text" />
        </Field>
        <Field label="Duración objetivo (minutos)" required>
          <Input value={targetDurationMinutes} onChange={setTargetDurationMinutes} placeholder="30" type="number" />
        </Field>
        <Field label="Descripción">
          <Textarea value={description} onChange={setDescription} placeholder="Patrón de cortes para programación matutina" rows={2} />
        </Field>
        <Field label="Categoría">
          <Select
            value={category}
            onChange={setCategory}
            full
            options={[
              { value: 'News', label: 'Noticias' },
              { value: 'Sports', label: 'Deportes' },
              { value: 'Drama', label: 'Drama' },
              { value: 'Documentary', label: 'Documental' },
              { value: "Children's", label: 'Infantil' },
              { value: 'Entertainment', label: 'Entretenimiento' },
            ]}
          />
        </Field>
        <Field label="Visibilidad">
          <SegmentedControl
            value={visibility}
            onChange={(v: 'private' | 'team' | 'public') => setVisibility(v)}
            options={[
              { value: 'private', label: 'Privada' },
              { value: 'team', label: 'Equipo' },
              { value: 'public', label: 'Pública' },
            ]}
          />
        </Field>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--muted-slate)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Cortes</span>
            <Btn variant="ghost" size="sm" icon="plus" onClick={addBreak}>Añadir corte</Btn>
          </div>
          {breaks.length === 0 ? (
            <div style={{ fontSize: 13, color: 'var(--muted-slate)', padding: '8px 0' }}>Aún no hay cortes. Añade uno para definir su posición (0 a 100%).</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {breaks.map((b, i) => (
                <div key={b.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 12, color: 'var(--muted-slate)', width: 64 }}>Posición %</span>
                  <Input
                    value={String(b.position)}
                    onChange={(val: string) => updateBreakPosition(i, val)}
                    type="number"
                    placeholder="0"
                  />
                  <Btn variant="ghost" size="sm" icon="x" onClick={() => removeBreak(i)} />
                </div>
              ))}
            </div>
          )}
        </div>
        {error && (
          <div style={{ fontSize: 13, color: 'var(--coral)', padding: '8px 12px', background: 'var(--pale-coral, rgba(255,128,128,0.08))', borderRadius: 8 }} role="alert">{error}</div>
        )}
      </div>
    </Modal>
  );
}

export function ProgrammeGuide() {
  const { toast } = useToast();
  // WIRE-2 (T330 / FR-009) — programme-guide export rides on the existing
  // `api.schedules.exportProgrammeGuide` query (convex/schedules.ts:760).
  // The query is currently a v.1-D stub returning `[]`; the wired-honest
  // behaviour is identical for any non-error response: serialise the rows
  // as JSON, wrap in a Blob, trigger a download via a synthesized `<a
  // download>` click. When the backend lands a real implementation, this
  // call site is unchanged.
  //
  // Honest-state contract (PRD §FR-013): we ONLY toast `success` once the
  // anchor click has fired with a real Blob — never up-front. `undefined`
  // (query still loading) disables the button; a thrown query surfaces as
  // the operator's connection-loss banner from the global error path.
  const guide = useQuery(api.schedules.exportProgrammeGuide, {}) as
    | unknown[]
    | undefined;
  const exportReady = Array.isArray(guide);
  // Real channel list for the "Canales" panel below. Capped at the first
  // 6 to preserve the prototype's compact layout (the right rail is fixed-
  // width); larger surfaces are scope for a successor wiring task.
  const allChannels = useChannelList();
  const channelsForGuide = (allChannels ?? []).slice(0, 6);
  const onExport = useCallback(() => {
    if (!exportReady) return;
    try {
      const json = JSON.stringify(guide, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      const stamp = new Date().toISOString().replace(/[:.]/g, '-');
      anchor.download = `programme-guide-${stamp}.json`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      // Revoke after the click — release the Blob URL the browser was
      // holding open. Defer with a microtask so the click handler has
      // finished consuming the URL.
      setTimeout(() => URL.revokeObjectURL(url), 0);
      toast({ variant: 'success', message: 'Exportación generada.' });
    } catch {
      toast({ variant: 'error', message: 'No se pudo generar la exportación.' });
    }
  }, [exportReady, guide, toast]);
  return (
    <div>
      <PageHead title="Guía de programación" description="Exportaciones de programación conformes a estándares para socios y proveedores de guía."
        actions={<Btn variant="solid" icon="download" disabled={!exportReady} onClick={onExport}>Generar exportación</Btn>} />
      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard label="Canales con guía" value={allChannels === undefined ? '—' : allChannels.length} icon="tv" />
        {/* Cobertura media / Exportaciones hoy require a backend
            aggregation that does not exist yet — render '—' honestly
            until those metrics ship. */}
        <StatCard label="Cobertura media" value={<Provenance kind="absent" detail="Sin agregación de cobertura en el backend todavía.">—</Provenance>} hint="por canal" icon="clock" />
        <StatCard label="Exportaciones hoy" value={<Provenance kind="absent" detail="Sin conteo de exportaciones del backend todavía.">—</Provenance>} icon="download" />
        <StatCard label="Formatos" value="3" icon="fileText" />
      </div>
      <div className="circa-cols-8-4">
        <Panel title="Canales" pad={0}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 100px', padding: '12px 20px', borderBottom: '1px solid var(--border-cool)', fontSize: 12, color: 'var(--muted-slate)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>
            <span>Canal</span><span>Horas</span><span>Última exp.</span><span></span>
          </div>
          {channelsForGuide.length === 0 && allChannels !== undefined && (
            <div style={{ padding: 24 }}><EmptyState icon="tv" title="Sin canales" body="Crea un canal para empezar a generar guías de programación." /></div>
          )}
          {channelsForGuide.map((c, i) => (
            <div key={c._id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 100px', padding: '14px 20px', alignItems: 'center', borderBottom: i < channelsForGuide.length - 1 ? '1px solid var(--lightest-gray)' : 'none', fontSize: 14 }}>
              <span>{c.name}</span>
              {/* Horas / Última exp. per channel are not yet aggregated by
                  the backend — see CIRCA-WIRE-5 (analytics). Render '—'
                  honestly. */}
              <span style={{ color: 'var(--muted-slate)' }}><Provenance kind="absent" detail="Horas por canal no agregadas por el backend todavía.">—</Provenance></span>
              <span style={{ color: 'var(--muted-slate)', fontSize: 13 }}><Provenance kind="absent" detail="Última exportación por canal no disponible del backend todavía.">—</Provenance></span>
              <Dropdown align="right" trigger={<Btn variant="ghost" icon="moreH" size="sm" />} items={[{ label: 'Descargar JSON', icon: 'download' }, { label: 'Descargar XMLTV', icon: 'download' }]} />
            </div>
          ))}
        </Panel>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <Panel title="Formatos">
            {['JSON', 'TiVo XMLTV', 'WURL JSON'].map(f => <div key={f} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', fontSize: 14 }}><Icon name="fileText" size={15} color="var(--muted-slate)" />{f}</div>)}
          </Panel>
          <MockBoundary detail="Exportaciones recientes y el medidor de límite son datos de demostración.">
            <Panel title="Límite de exportación"><div style={{ marginBottom: 8 }}><Progress value={20} /></div><span style={{ fontSize: 13, color: 'var(--muted-slate)' }}>12 de 60 exportaciones/hora usadas</span></Panel>
          </MockBoundary>
          <MockBoundary detail="Exportaciones recientes y el medidor de límite son datos de demostración.">
            <Panel title="Exportaciones recientes" pad={0}>
              {[['Noticias 24', 'XMLTV', 'good'], ['Ritmo Latino', 'JSON', 'good'], ['Deportes Vivo', 'WURL', 'warn']].map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px', borderBottom: i < 2 ? '1px solid var(--lightest-gray)' : 'none' }}>
                  <Icon name="fileText" size={15} color="var(--muted-slate)" />
                  <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r[0]}</div><span className="t-mono" style={{ fontSize: 10, color: 'var(--muted-slate)', textTransform: 'none' }}>{r[1]} · hace {i + 1} h</span></div>
                  <Pill tone={r[2]} size="sm">{r[2] === 'good' ? 'OK' : 'Parcial'}</Pill>
                </div>
              ))}
            </Panel>
          </MockBoundary>
        </div>
      </div>
    </div>
  );
}

